<template>
  <div class="space-y-4">
    <!-- Header -->
    <LayerHeader
      :name="localLayer.name"
      :locked="localLayer.locked"
      @update:name="updateName"
      @update:locked="updateLocked"
      @blur="updateLayer"
    />

    <!-- YouTube URL -->
    <div v-if="localLayer.type === 'youtube'" class="space-y-1.5">
      <label class="text-xs font-medium text-muted-foreground">YouTube URL</label>
      <Input
        :model-value="localLayer.content_source"
        placeholder="https://www.youtube.com/embed/..."
        class="h-8 text-xs font-mono"
        @update:model-value="updateYoutubeUrl"
        @blur="updateLayer"
        @keyup.enter="updateLayer"
      />
    </div>

    <!-- Transform -->
    <TransformControls
      :x="localLayer.x"
      :y="localLayer.y"
      :width="localLayer.width"
      :height="localLayer.height"
      :rotation="localLayer.effects.rotation"
      :disabled="localLayer.locked"
      :isInherited="isInherited"
      :xOverridden="fieldOverrideChecks.x"
      :yOverridden="fieldOverrideChecks.y"
      :widthOverridden="fieldOverrideChecks.width"
      :heightOverridden="fieldOverrideChecks.height"
      :rotationOverridden="fieldOverrideChecks.rotation"
      :parentSceneName="localLayer.parent_scene_name"
      @update="updateTransform"
      @resetField="handleResetField"
    />

    <!-- Object Fit -->
    <ObjectFitControls
      v-if="isImageOrYouTube"
      :objectFit="localLayer.objectFit"
      :type="layer.type as 'image' | 'youtube'"
      :isInherited="isInherited"
      :objectFitOverridden="fieldOverrideChecks.objectFit"
      :parentSceneName="localLayer.parent_scene_name"
      @update="updateObjectFit"
      @resetField="handleResetField"
    />

    <!-- Crop -->
    <CropControls
      v-if="isImageOrYouTube"
      :crop="localLayer.crop || { top: 0, left: 0, right: 0, bottom: 0 }"
      :isInherited="isInherited"
      :topOverridden="fieldOverrideChecks.cropTop"
      :rightOverridden="fieldOverrideChecks.cropRight"
      :bottomOverridden="fieldOverrideChecks.cropBottom"
      :leftOverridden="fieldOverrideChecks.cropLeft"
      :parentSceneName="localLayer.parent_scene_name"
      @update="updateCrop"
      @resetField="handleResetField"
    />

    <!-- Basic Effects -->
    <BasicEffects
      :opacity="localLayer.effects.opacity"
      :blendMode="localLayer.effects.blendMode"
      :isInherited="isInherited"
      :opacityOverridden="fieldOverrideChecks.opacity"
      :blendModeOverridden="fieldOverrideChecks.blendMode"
      :parentSceneName="localLayer.parent_scene_name"
      @updateOpacity="updateOpacity"
      @updateBlendMode="updateBlendMode"
      @resetField="handleResetField"
    />

    <!-- Animation -->
    <AnimationControls
      :animation="localLayer.effects.animation"
      :isInherited="isInherited"
      :animationOverridden="fieldOverrideChecks.animation"
      :parentSceneName="localLayer.parent_scene_name"
      @update="updateAnimation"
      @resetField="handleResetField"
    />

    <!-- Color Adjustments -->
    <ColorAdjustments
      v-if="isImageOrYouTube"
      :hue="localLayer.effects.hue"
      :tint="localLayer.effects.tint"
      :saturation="localLayer.effects.saturation"
      :brightness="localLayer.effects.brightness"
      :contrast="localLayer.effects.contrast"
      :blur="localLayer.effects.blur"
      :isInherited="isInherited"
      :hueOverridden="fieldOverrideChecks.hue"
      :tintOverridden="fieldOverrideChecks.tint"
      :saturationOverridden="fieldOverrideChecks.saturation"
      :brightnessOverridden="fieldOverrideChecks.brightness"
      :contrastOverridden="fieldOverrideChecks.contrast"
      :blurOverridden="fieldOverrideChecks.blur"
      :parentSceneName="localLayer.parent_scene_name"
      @updateHue="updateHue"
      @updateTint="updateTint"
      @updateSaturation="updateSaturation"
      @updateBrightness="updateBrightness"
      @updateContrast="updateContrast"
      @updateBlur="updateBlur"
      @resetField="handleResetField"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { Input } from '@/components/ui/input'
import LayerHeader from './controls/LayerHeader.vue'
import TransformControls from './controls/TransformControls.vue'
import ObjectFitControls from './controls/ObjectFitControls.vue'
import CropControls from './controls/CropControls.vue'
import BasicEffects from './controls/BasicEffects.vue'
import AnimationControls from './controls/AnimationControls.vue'
import ColorAdjustments from './controls/ColorAdjustments.vue'
import { useLayerInheritance } from '@/composables/useLayerInheritance'
import type { DisplayLayer } from '@/stores/sceneDisplayConfigs'

interface Props {
  layer: DisplayLayer
  parentLayer?: DisplayLayer
}

const props = defineProps<Props>()

const emit = defineEmits<{
  updateLayer: [layer: DisplayLayer]
  resetField: [fieldPath: string]
}>()

// Local copy for editing
const localLayer = ref<DisplayLayer>({ 
  ...props.layer,
  crop: props.layer.crop || { top: 0, left: 0, right: 0, bottom: 0 }
})

// Parent layer as ref
const parentLayerRef = computed(() => props.parentLayer)

// Use inheritance tracking - now reactive to changes in localLayer
const { isInherited, isFieldOverridden, getParentValue } = useLayerInheritance({
  layer: localLayer,
  parentLayer: parentLayerRef
})

// Debug logging
watch([isInherited, parentLayerRef], ([inherited, parent]) => {
  if (inherited) {
    console.log('[LayerControls] Inheritance status:', {
      layerName: localLayer.value.name,
      isInherited: inherited,
      hasParentLayer: !!parent,
      parentLayer: parent,
      currentX: localLayer.value.x,
      parentX: parent?.x
    })
  }
}, { immediate: true })

// Create computed wrappers for field checks to ensure reactivity
// Add safety check to prevent accessing during unmount
const fieldOverrideChecks = computed(() => ({
  x: isFieldOverridden('x'),
  y: isFieldOverridden('y'),
  width: isFieldOverridden('width'),
  height: isFieldOverridden('height'),
  rotation: isFieldOverridden('effects.rotation'),
  opacity: isFieldOverridden('effects.opacity'),
  blendMode: isFieldOverridden('effects.blendMode'),
  animation: isFieldOverridden('effects.animation'),
  hue: isFieldOverridden('effects.hue'),
  tint: isFieldOverridden('effects.tint'),
  saturation: isFieldOverridden('effects.saturation'),
  brightness: isFieldOverridden('effects.brightness'),
  contrast: isFieldOverridden('effects.contrast'),
  blur: isFieldOverridden('effects.blur'),
  objectFit: isFieldOverridden('objectFit'),
  cropTop: isFieldOverridden('crop.top'),
  cropRight: isFieldOverridden('crop.right'),
  cropBottom: isFieldOverridden('crop.bottom'),
  cropLeft: isFieldOverridden('crop.left'),
}))

// Computed helper
const isImageOrYouTube = computed(() => 
  localLayer.value.type === 'image' || localLayer.value.type === 'youtube'
)

// Watch for prop changes
watch(() => props.layer, (newLayer) => {
  localLayer.value = { 
    ...newLayer,
    crop: newLayer.crop || { top: 0, left: 0, right: 0, bottom: 0 }
  }
}, { deep: true })

// Emit helper
const updateLayer = () => {
  emit('updateLayer', localLayer.value)
}

// Header handlers
const updateName = (name: string) => {
  localLayer.value.name = name
}

const updateYoutubeUrl = (value: string | number) => {
  const url = String(value)
  localLayer.value.content_source = url
  localLayer.value.content = url
}

const updateLocked = (value: boolean) => {
  localLayer.value.locked = value
  updateLayer()
}

// Transform handlers
const updateTransform = (transform: Partial<DisplayLayer> & { rotation?: number }) => {
  Object.assign(localLayer.value, transform)
  if ('rotation' in transform && transform.rotation !== undefined) {
    localLayer.value.effects.rotation = transform.rotation
  }
  updateLayer()
}

// Object fit handler
const updateObjectFit = (value: string) => {
  localLayer.value.objectFit = value as any
  updateLayer()
}

// Crop handler
const updateCrop = (crop: { top: number; right: number; bottom: number; left: number }) => {
  localLayer.value.crop = crop
  updateLayer()
}

// Basic effects handlers
const updateOpacity = (value: number) => {
  localLayer.value.effects.opacity = value
  updateLayer()
}

const updateBlendMode = (value: string) => {
  localLayer.value.effects.blendMode = value
  updateLayer()
}

// Animation handler
const updateAnimation = (animation: any) => {
  localLayer.value.effects.animation = animation
  updateLayer()
}

// Color adjustment handlers
const updateHue = (value: number) => {
  localLayer.value.effects.hue = value
  updateLayer()
}

const updateTint = (value: string) => {
  localLayer.value.effects.tint = value
  updateLayer()
}

const updateSaturation = (value: number) => {
  localLayer.value.effects.saturation = value
  updateLayer()
}

const updateBrightness = (value: number) => {
  localLayer.value.effects.brightness = value
  updateLayer()
}

const updateContrast = (value: number) => {
  localLayer.value.effects.contrast = value
  updateLayer()
}

const updateBlur = (value: number) => {
  localLayer.value.effects.blur = value
  updateLayer()
}

// Reset field handler
const handleResetField = (fieldPath: string) => {
  if (!props.parentLayer) return

  const parts = fieldPath.split('.')
  let target: any = localLayer.value
  let parentValue: any = props.parentLayer

  // Navigate to parent object
  for (let i = 0; i < parts.length - 1; i++) {
    target = target[parts[i]]
    parentValue = parentValue[parts[i]]
  }

  // Set the value from parent
  const lastPart = parts[parts.length - 1]
  target[lastPart] = parentValue[lastPart]
  
  // Emit reset event for parent to handle override deletion
  emit('resetField', fieldPath)
  updateLayer()
}
</script>